import { useRef, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

export const DND_TYPES = {
  SECTION: 'FORM_BUILDER_SECTION',
  SUBSECTION: 'FORM_BUILDER_SUBSECTION',
  QUESTION_GROUP: 'FORM_BUILDER_QUESTION_GROUP',
  QUESTION: 'FORM_BUILDER_QUESTION',
} as const;

export interface SortableDragItem {
  id: string;
  index: number;
  containerId?: string;
  type: string;
  label: string;
}

interface UseSortableItemParams {
  type: string;
  id: string;
  index: number;
  label: string;
  onMove: (fromIndex: number, toIndex: number) => void;
  containerId?: string;
  /** When true, drag does not start (drop targets still accept reorder). */
  disabled?: boolean;
}

export function useSortableItem({
  type,
  id,
  index,
  label,
  onMove,
  containerId,
  disabled = false,
}: UseSortableItemParams) {
  const itemRef = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type,
    item: (): SortableDragItem => ({ id, index, containerId, type, label }),
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    canDrag: () => !disabled,
  });

  const [, drop] = useDrop({
    accept: type,
    canDrop: (item: SortableDragItem) => {
      if (containerId && item.containerId !== containerId) return false;
      return true;
    },
    hover: (item: SortableDragItem, monitor) => {
      if (!itemRef.current) return;
      if (containerId && item.containerId !== containerId) return;

      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = itemRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      onMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  drop(itemRef);

  return { itemRef, handleRef: drag, isDragging };
}
